import { DndContext, type DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { TField } from '@timeedit/types';
import { Button } from '@timeedit/ui-components';
import { List, Modal } from '@timeedit/ui-components/antd';
import SortableFieldRow from './SortableFieldRow';

type SortableFieldModalProps = {
  visible: boolean;
  setVisible: (bool: boolean) => void;
  fields: TField[];
  onChangeOrder: (moveField: TField, targetField: TField) => void;
  onSave: () => void;
  onDiscard: () => void;
  hasChanges: boolean;
};

const SortableFieldModal = ({
  visible,
  setVisible,
  fields,
  onChangeOrder,
  onSave,
  onDiscard,
  hasChanges,
}: SortableFieldModalProps) => {
  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    // If over is null, it means the item was dragged outside of the list, no action
    if (!over) return;

    // If active and over are the same the item dragged is on the same position, no action
    if (active.id === over.id) return;

    const moveField = fields.find((field) => field.id === active.id);
    const targetField = fields.find((field) => field.id === over.id);

    // If moveField or targetField is null, no action
    if (!moveField || !targetField) return;
    onChangeOrder(moveField, targetField);
  };

  return (
    <Modal
      title="Change order"
      open={visible}
      onCancel={() => {
        setVisible(false);
        onDiscard();
      }}
      width={{
        xs: '90%',
        sm: '80%',
        md: '70%',
        lg: '60%',
        xl: '50%',
        xxl: '40%',
      }}
      footer={[
        <Button disabled={!hasChanges} key="back" onClick={() => onDiscard()}>
          Discard
        </Button>,
        <Button
          disabled={!hasChanges}
          key="submit"
          type="primary"
          onClick={() => {
            onSave();
            setVisible(false);
          }}
        >
          Save
        </Button>,
      ]}
    >
      <DndContext onDragEnd={onDragEnd}>
        <SortableContext items={fields} strategy={verticalListSortingStrategy}>
          <List
            itemLayout="vertical"
            dataSource={fields}
            renderItem={(field) => {
              return <SortableFieldRow key={field.id} field={field} />;
            }}
          />
        </SortableContext>
      </DndContext>
    </Modal>
  );
};

export default SortableFieldModal;
